import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { limitBullets } from '../../../../eila-global/js/slider/sliderHelpers.js';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

export function initHorizontalInfoSlides() {
    const containers = document.querySelectorAll('.horizontalInfoSlides');
    const MOBILE_BREAKPOINT = 1025;

    if (!containers.length) {
        return;
    }

    containers.forEach((container) => {
        const wrapper = container.querySelector('.swiper-wrapper');
        const wrapperOuter = container.querySelector('.swiper-wrapper-outer');
        const slides = container.querySelectorAll('.swiper-slide');
        const pagination = container.querySelector('.swiper-pagination');

        if (!wrapper || !slides.length) {
            return;
        }

        let pinTrigger = null;
        let slideTriggers = [];
        let bullets = [];

        const setupDesktop = () => {
            const multiplicator = 0.6;
            const scrollPerSlide = window.innerHeight * multiplicator;
            const lastSlideHold = window.innerHeight;
            const totalScrollDistance = (scrollPerSlide * slides.length) + lastSlideHold;

            // Container-Höhe setzen
            container.style.height = `${totalScrollDistance}px`;

            // Pagination Setup
            if (pagination) {
                pagination.innerHTML = '';

                slides.forEach((_, index) => {
                    const dot = document.createElement('span');
                    dot.classList.add('swiper-pagination-bullet');
                    if (index === 0) {
                        dot.classList.add('swiper-pagination-bullet-active');
                    }
                    pagination.appendChild(dot);
                });

                bullets = Array.from(pagination.querySelectorAll('.swiper-pagination-bullet'));
                updatePagination(bullets, 0);
            }

            // Pin den wrapperOuter für die gesamte Scroll-Distanz
            pinTrigger = ScrollTrigger.create({
                trigger: container,
                start: 'top top',
                end: () => `+=${totalScrollDistance - window.innerHeight}`,
                pin: wrapperOuter,
                anticipatePin: 1,
                invalidateOnRefresh: true
            });

            // Elemente für Parallax-Effekt sammeln
            const slideElements = [];
            slides.forEach((slide) => {
                slideElements.push({
                    images: Array.from(slide.querySelectorAll('.horizontalInfoSlide__img:not(.horizontalInfoSlide__img--1)')),
                    headline: slide.querySelector('.horizontalInfoSlide__headline'),
                    content: slide.querySelector('.horizontalInfoSlide__contentWrapper')
                });
            });

            // Für jeden Slide einen Trigger erstellen
            slides.forEach((slide, index) => {
                const trigger = ScrollTrigger.create({
                    trigger: container,
                    start: () => `top+=${index * scrollPerSlide} top`,
                    end: () => `top+=${(index + 1) * scrollPerSlide} top`,
                    onEnter: () => {
                        scrollToSlide(index);
                        updatePagination(bullets, index);
                    },
                    onEnterBack: () => {
                        scrollToSlide(index);
                        updatePagination(bullets, index);
                    }
                });

                slideTriggers.push(trigger);
            });

            // Funktion um horizontal zum Slide zu scrollen
            function scrollToSlide(index) {
                const targetScrollLeft = index * window.innerWidth;
                const direction = targetScrollLeft > wrapper.scrollLeft ? 1 : -1;

                // Wrapper animieren
                gsap.to(wrapper, {
                    scrollLeft: targetScrollLeft,
                    duration: 1.8,
                    ease: 'power2.out'
                });

                // Kein Parallax wenn wir beim ersten Slide starten
                if (index === 0 && wrapper.scrollLeft === 0) return;

                // Kein Parallax wenn wir von unten zum letzten Slide kommen
                const lastSlideIndex = slides.length - 1;
                const lastSlideScrollLeft = lastSlideIndex * window.innerWidth;
                if (index === lastSlideIndex && wrapper.scrollLeft >= lastSlideScrollLeft) return;

                // Parallax für alle Slide-Elemente
                slideElements.forEach((elements) => {
                    // Bilder - am langsamsten, stärkster Versatz
                    elements.images.forEach((img, imgIndex) => {
                        const parallaxOffset = direction * 150 * (imgIndex + 1);
                        gsap.fromTo(img,
                            { x: parallaxOffset },
                            {
                                x: 0,
                                duration: 2,
                                ease: 'power2.out'
                            }
                        );
                    });

                    // Content - kleinster Versatz
                    if (elements.content) {
                        gsap.fromTo(elements.content,
                            { x: direction * 150 },
                            {
                                x: 0,
                                duration: 2.2,
                                ease: 'power2.out'
                            }
                        );
                    }

                    // Headline - mittlerer Versatz
                    if (elements.headline) {
                        gsap.fromTo(elements.headline,
                            { x: direction * 200 },
                            {
                                x: 0,
                                duration: 2.4,
                                ease: 'power2.out'
                            }
                        );
                    }
                });
            }

            // Click-Handler für Pagination - nur horizontal scrollen
            if (bullets.length > 0) {
                bullets.forEach((bullet, index) => {
                    bullet.addEventListener('click', () => {
                        scrollToSlide(index);
                        updatePagination(bullets, index);
                    });
                });
            }
        };

        const setupMobile = () => {
            container.removeAttribute('style');
            wrapper.removeAttribute('style');
        };

        const cleanup = () => {
            if (pinTrigger) {
                pinTrigger.kill();
                pinTrigger = null;
            }

            slideTriggers.forEach(trigger => trigger.kill());
            slideTriggers = [];

            container.style.height = '';
            gsap.set(wrapper, { scrollLeft: 0 });
        };

        // MediaQuery für Breakpoint-Wechsel
        const mediaQuery = window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT}px)`);

        const handleBreakpointChange = (e) => {
            cleanup();

            if (e.matches) {
                setupDesktop();
            } else {
                setupMobile();
            }
        };

        // Initial setup
        if (mediaQuery.matches) {
            setupDesktop();
        } else {
            setupMobile();
        }

        mediaQuery.addEventListener('change', handleBreakpointChange);
    });
}

function updatePagination(bullets, activeIndex) {
    bullets.forEach((bullet, index) => {
        if (index === activeIndex) {
            bullet.classList.add('swiper-pagination-bullet-active');
        } else {
            bullet.classList.remove('swiper-pagination-bullet-active');
        }
    });

    // Bullets are hand built here, so the Swiper pagination limit does not apply
    limitBullets(bullets, activeIndex);
}